import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { classNames } from "primereact/utils";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { Checkbox } from 'primereact/checkbox';
import { useEffect, useRef, useState } from "react";
import { Calendar } from "primereact/calendar";
import ProductService from "../../services/products/products.service";
import { ProductDetailModel } from "../../components/admin/products";
import { Toast, ToastMessage } from "primereact/toast";
import { InputNumber } from 'primereact/inputnumber';
import vendorService from "../../services/vendors/vendor.service";
import { VendorSummaryDTO } from "../../models/vendors/vendor-model";

interface EditProductProps {
    isUpdate: boolean;
    handleOnSuccess: any;
    updateProductInfo: ProductDetailModel | undefined | null;
    subCate?: any;
}

const ProductInfo: React.FC<EditProductProps> = ({ isUpdate, handleOnSuccess, updateProductInfo, subCate }) => {
    const { t } = useTranslation();
    const toast = useRef<Toast>(null);
    const [categories, setCategories] = useState<any>();
    const [subCategories, setSubCategories] = useState<any>(subCate);
    const [selectedCategory, setSelectedCategory] = useState<any>();
    const [selectSubCategory, setSelectedSubCategory] = useState<any>();    
    const [filteredSubCategories, setFilteredSubCategories] = useState<any>();
    const [mesuretype, setMessuretype] = useState<string>("");
    const [vendorList, setVendorList] = useState<{ label: string; value: number }[]>([]);

    const showMessage = (label: string, message: string, severity: ToastMessage['severity']) => {
        toast.current?.show({ severity: severity, summary: label, detail: message, life: 3000 });
    }

    const formik: any = useFormik({
        initialValues: {
            name: "",
            vendorID: null,
            categoryID: null,
            subCategoryID: null,
            code: "",
            weight: null,
            description: "",
            usage: "",
            minOrderQty: null,
            unitPrice: null,
            gst: null,
            hsN_Code: "",
            subscriptionAvailable: false,
            activeStartDate: null,
            activeEndDate: null,
            leadTime: null,
            canExchange: false,
            canReturn: false,
            courierAvailable: false,
            shelfLife: "",
            inStock: 1,
            isDeleted: false,
            isHomeDeliveryAvailable: false,
            orderMultiples: "",
            volume: null,
            measurement: ""
        },
        validateOnChange: false,  
        validate: (data: any) => {
            let errors: any = {};

            if (!data.categoryID) {
                errors.categoryID = t("Category is required");
            }

            if (!data.measurement) {
                errors.measurement = t("measurement_is_required");
            }

            if (data.volume === null || data.volume === undefined || data.volume === '') {
                errors.volume = t("Volume is required");
            }

            if (!data.subCategoryID) {
                errors.subCategoryID = t("Sub category is required");
            }

            if (!data.vendorID) {
                errors.vendorName = t("Vendor is required");
            }

            if (!data.name) {
                errors.name = t("Name is required");
            }

            // FIXED: Allow negative values, 0, and positive values for unitPrice
            if (data.unitPrice === null || data.unitPrice === undefined || data.unitPrice === '') {
                errors.unitPrice = t("Unit price is required");
            }

            // FIXED: Allow negative values, 0, and positive values for weight
            if (data.weight === null || data.weight === undefined || data.weight === '') {
                errors.weight = t("Weight  is required");
            }

            // FIXED: Allow negative values, 0, and positive values for minOrderQty
            if (data.minOrderQty === null || data.minOrderQty === undefined || data.minOrderQty === '') {
                errors.minOrderQty = t("Min order quantity is required");
            }

            if (!data.measurementTypeID) {
                errors.measurementTypeID = t("Measurement type is required");
            }

            // FIXED: Allow negative values for GST
            if (data.gst === null || data.gst === undefined || data.gst === '') {
                errors.gst = t("GST is required");
            } else if (!/^-?\d{1,2}(\.\d{1,2})?$/.test(data.gst)) {
                errors.gst = t("gst_should_be_in_2_digits");
            }

            if (!data.orderMultiples) {
                errors.orderMultiples = t("Order multiples is required");
            }
                if (data.activeStartDate && data.activeEndDate) {
        const startDate = new Date(data.activeStartDate);
        const endDate = new Date(data.activeEndDate);

        if (startDate > endDate) {
            errors.activeEndDate = t("Active end date cannot be before active start date");
        }
    }

            return errors;
        },
        onSubmit: async (data: any) => {
            // Manually validate before submission
            const errors: any = {};
                
            if (!data.categoryID) errors.categoryID = t("Category is required");
            if (!data.measurement) errors.measurement = t("measurement_is_required");
            if (data.volume === null || data.volume === undefined || data.volume === '') errors.volume = t("Volume is required");
            if (!data.subCategoryID) errors.subCategoryID = t("Sub category is required");
            if (!data.vendorID) errors.vendorName = t("Vendor is required");
            if (!data.name) errors.name = t("Name is required");
            if (data.unitPrice === null || data.unitPrice === undefined || data.unitPrice === '') errors.unitPrice = t("Unit price is required");
            if (data.weight === null || data.weight === undefined || data.weight === '') errors.weight = t("Weight  is required");
            if (data.minOrderQty === null || data.minOrderQty === undefined || data.minOrderQty === '') errors.minOrderQty = t("Min order quantity is required");
            if (!data.measurementTypeID) errors.measurementTypeID = t("Measurement type is required");
            if (data.activeStartDate && data.activeEndDate) {
    const startDate = new Date(data.activeStartDate);
    const endDate = new Date(data.activeEndDate);

    if (startDate > endDate) {
        errors.activeEndDate = t("Active end date cannot be before active start date");
    }
}
            if (data.gst === null || data.gst === undefined || data.gst === '') {
                errors.gst = t("GST is required");
            } else if (!/^-?\d{1,2}(\.\d{1,2})?$/.test(data.gst)) {
                errors.gst = t("gst_should_be_in_2_digits");
            }
            if (!data.orderMultiples) errors.orderMultiples = t("Order multiples is required");

            if (Object.keys(errors).length > 0) {
                formik.setErrors(errors);
                return;
            }
            
            var product = {
                "productID": data.productID,
                "vendorID": data.vendorID,
                "subCategoryID": data.subCategoryID,
                "measurementTypeID": data.measurementTypeID,
                "name": data.name,
                "unitPrice": data.unitPrice,
                "code": data.code,
                "description": data.description,
                "usage": data.usage,
                "weight": data.weight,
                "minOrderQty": data.minOrderQty,
                "orderMultiples": data.orderMultiples,
                "gst": data.gst,
                "hsN_Code": data.hsN_Code,
                "subscriptionAvailable": data.subscriptionAvailable,
                "activeStartDate": data.activeStartDate,
                "activeEndDate": data.activeEndDate,
                "leadTime": data.leadTime,
                "canExchange": data.canExchange,
                "canReturn": data.canReturn, 
                "courierAvailable": data.courierAvailable,
                "shelfLife": data.shelfLife,
                "inStock": data.inStock,
                "isHomeDeliveryAvailable": data.isHomeDeliveryAvailable,
                "volume": data.volume,
                "measurement": data.measurement
            };
            
            let response = isUpdate ? await ProductService.saveProduct(product) : await ProductService.saveNewProduct(product);
            
            if (response.isSuccess) {
                handleOnSuccess();
                setTimeout(() => {
                    window.location.reload();
                }, 500);
            } else {
                showMessage('Update Products', response.message + ': Error in update product', 'error');
            }
        }
    });

    useEffect(() => {
        fetchCategory();
        fetchSubCategory();
        fetchVendors(); 

        if (isUpdate) {
            setProductData();
        }
    }, []);

    function fetchCategory() {
        ProductService.getCategory().then((res) => {
            setCategories(res);
        });
    }

    function fetchSubCategory() {
        ProductService.getSubCategory().then((res) => {
            setSubCategories(res);
        })
    }
    
    const fetchVendors = () => {
        vendorService.getAllVendors().then((res: VendorSummaryDTO[]) => {
            const mapped = res.map((v) => ({
                label: `${v.firmInfo}${v.officeContactNbr ? `, ${v.officeContactNbr}` : ""}`,
                value: v.vendorID
            }));
            setVendorList(mapped);
        });
    };

    function setProductData() {
        if (updateProductInfo) {
            let categoryId: number = updateProductInfo.categoryID;
            setSelectedCategory(categoryId);
            const filterData: any = subCategories.filter((eachItem: any) => eachItem.categoryID === categoryId)
            setFilteredSubCategories(filterData);
            formik.setValues({
                productID: updateProductInfo.productID,
                name: updateProductInfo.name,
                vendorID: updateProductInfo.vendorID,
                categoryID: updateProductInfo.categoryID,
                subCategoryID: updateProductInfo.subCategoryID,
                measurementTypeID: updateProductInfo.measurementTypeID,
                code: updateProductInfo.code,
                weight: updateProductInfo.weight,
                description: updateProductInfo.description,
                usage: updateProductInfo.usage,
                minOrderQty: updateProductInfo.minOrderQty,
                unitPrice: updateProductInfo.unitPrice,
                gst: updateProductInfo.gst,
                hsN_Code: updateProductInfo.hsN_Code,
                subscriptionAvailable: updateProductInfo.subscriptionAvailable,
                activeStartDate: updateProductInfo.activeStartDate,
                activeEndDate: updateProductInfo.activeEndDate,
                leadTime: updateProductInfo.leadTime,
                canExchange: updateProductInfo.canExchange,
                canReturn: updateProductInfo.canReturn,
                courierAvailable: updateProductInfo.courierAvailable,
                shelfLife: updateProductInfo.shelfLife,
                inStock: updateProductInfo.inStock,
                isDeleted: updateProductInfo.isDeleted,
                isHomeDeliveryAvailable: updateProductInfo.isHomeDeliveryAvailable,
                orderMultiples: updateProductInfo.orderMultiples,
                volume: updateProductInfo.volume,
                measurement: updateProductInfo.measurement
            });
        }
    }

    const measurementTypeItems = [
        { "name": "Litre", "measurementTypeID": 1 },
        { "name": "Kilogram", "measurementTypeID": 2 },
        { "name": "Grams", "measurementTypeID": 3 },
        { "name": "Pieces", "measurementTypeID": 4 }
    ];

    const isFormFieldInvalid = (name: any) => !!(formik.touched[name] && formik.errors[name]);

    const getFormErrorMessage = (name: any) => {
        return isFormFieldInvalid(name) ? <small className="p-error">{formik.errors[name]}</small> : "";
    };

    const categoryOnChange = (e: DropdownChangeEvent) => {
        setSelectedCategory(e.value);
        const filterSubCategories = subCategories.filter((eachItem: any) => eachItem.categoryID == e.value);
        setFilteredSubCategories(filterSubCategories);
    }

    const subCategoryOnChange = (e: DropdownChangeEvent) => {
        setSelectedSubCategory(e.value);
    }

    const measureTypeOnChange = (e: DropdownChangeEvent) => {
        const selectedItem = measurementTypeItems.find(item => item.measurementTypeID === e.value);
        if (selectedItem) {
            setMessuretype(selectedItem.name);
        }
    };    

    return (
        <><Toast ref={toast} position="top-center" />
            <div className="col-12  flex align-items-stretch flex-wrap">
                <div className="flex flex-1 bg-white align-items-center justify-content-center">                
                    <form onSubmit={formik.handleSubmit}>
                        <h5 className='border-round mb-3 order-top-color p-2 panel-header-text text-center'>
                            {isUpdate ? t('update_product') : t('register_product')}
                        </h5>               
                        <div className="p-fluid formgrid grid">
                            <div className="col-12">
                                <div className="grid">
                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <div className="field">
                                            <label htmlFor="categoryID" className="label-semi-bold">{t('product_category')}<span className="required">*</span></label>
                                            <Dropdown
                                                id="categoryID"
                                                name="categoryID"
                                                onBlur={formik.handleBlur}
                                                className={`pl-0 ${classNames({ 'p-invalid': formik.touched.categoryID && formik.errors.categoryID })}`}
                                                value={formik.values.categoryID}
                                                onChange={(e) => {
                                                    formik.setFieldValue('categoryID', e.value);
                                                    formik.setFieldTouched('categoryID', true, false);
                                                    categoryOnChange(e);
                                                }}
                                                style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                options={categories}
                                                optionLabel="name"
                                                optionValue="categoryID"
                                                placeholder="Select Category"
                                            />
                                            {formik.touched.categoryID && formik.errors.categoryID && (
                                                <small className="p-error">{formik.errors.categoryID}</small>
                                            )}
                                        </div>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <div className="field">
                                            <label htmlFor="subCategoryID" className="label-semi-bold">{t('product_sub_category')}<span className="required">*</span></label>
                                            <Dropdown
                                                id="subCategoryID"
                                                name="subCategoryID"
                                                onBlur={formik.handleBlur}
                                                className={`pl-0 ${classNames({ 'p-invalid': formik.touched.subCategoryID && formik.errors.subCategoryID })}`}
                                                value={formik.values.subCategoryID}
                                                onChange={(e) => {
                                                    formik.setFieldValue('subCategoryID', e.value);
                                                    formik.setFieldTouched('subCategoryID', true, false);
                                                    subCategoryOnChange(e);
                                                }}
                                                style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                options={filteredSubCategories}
                                                optionLabel="name"
                                                optionValue="subCategoryID"
                                                placeholder="Select Sub Category" 
                                            />
                                            {formik.touched.subCategoryID && formik.errors.subCategoryID && (
                                                <small className="p-error">{formik.errors.subCategoryID}</small>
                                            )}
                                        </div>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <div className="field">
                                            <label className="block mb-1 font-medium">Vendor</label>
                                            <Dropdown
                                                id="vendorID"
                                                name="vendorID"
                                                onBlur={formik.handleBlur}
                                                className={classNames({ "p-invalid": formik.touched.vendorID && formik.errors.vendorID })}
                                                value={formik.values.vendorID}
                                                options={vendorList}
                                                onChange={(e) => {
                                                    formik.setFieldValue("vendorID", e.value);
                                                    formik.setFieldTouched("vendorID", true, false);
                                                }}
                                                placeholder="Select Vendor"
                                                filter
                                                filterBy="label"
                                                showClear
                                                style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                            />
                                            {formik.touched.vendorID && formik.errors.vendorID && (
                                                <small className="p-error">{formik.errors.vendorID}</small>
                                            )}
                                        </div>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <div className="field">
                                            <label htmlFor="measurementTypeID" className="label-semi-bold">{t('measurement_Type')}<span className="required">*</span></label>
                                            <Dropdown
                                                id="measurementTypeID"
                                                name="measurementTypeID"
                                                onBlur={formik.handleBlur}
                                                className={`pl-1 ${classNames({ 'p-invalid': formik.touched.measurementTypeID && formik.errors.measurementTypeID })}`}
                                                value={formik.values.measurementTypeID}
                                                onChange={(e) => {
                                                    formik.setFieldValue('measurementTypeID', e.value);
                                                    formik.setFieldTouched('measurementTypeID', true, false);
                                                    measureTypeOnChange(e);
                                                }}
                                                style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                options={measurementTypeItems}
                                                optionLabel="name"
                                                optionValue="measurementTypeID"
                                                placeholder="Select Measurement Type"
                                            />
                                            {formik.touched.measurementTypeID && formik.errors.measurementTypeID && (
                                                <small className="p-error">{formik.errors.measurementTypeID}</small>
                                            )}
                                        </div>
                                    </div>

                                    <div className="col-12 lg:col-6 mb-2 md:col-6 mb-3 px-3">
                                        <div className="field">
                                            <label htmlFor="name" className="label-semi-bold">{t('product_name')}<span className="required">*</span></label>
                                            <span className="p-input-icon-left">
                                                <InputText type="text" id="name" name="name" 
                                                    value={formik.values.name}
                                                    onBlur={formik.handleBlur}
                                                    onChange={(e) => {
                                                        const cleanedValue = e.target.value.replace(/\s\s+/g, ' ');
                                                        formik.setFieldValue('name', cleanedValue);
                                                    }}
                                                    className={`pl-1 ${classNames({ 'p-invalid': formik.touched.name && formik.errors.name })} tracking-normal whitespace-normal`}
                                                    placeholder={t('Product Name')}
                                                    style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                 />
                                            </span>
                                            {formik.touched.name && formik.errors.name && (
                                                <small className="p-error">{formik.errors.name}</small>
                                            )}
                                        </div>
                                    </div>

                                    <div className="col-12 lg:col-6 mb-2 md:col-6 mb-3 px-3">
                                        <label htmlFor="unitPrice" className="label-semi-bold">{t('unit_price')}<span className="required">*</span></label>
                                        <span className="p-input-icon-left">
                                            <InputNumber
                                                id="unitPrice"
                                                name="unitPrice"
                                                value={formik.values.unitPrice}
                                                onBlur={formik.handleBlur}
                                                onValueChange={e => {
                                                    formik.setFieldValue('unitPrice', e.value);
                                                }}
                                                mode="decimal"
                                                minFractionDigits={2}
                                                maxFractionDigits={2}
                                                placeholder="Enter Unit Price"
                                                style={{ border: '1px solid #ced4da', borderRadius: '4px', width: '100%' }}
                                            />
                                        </span>
                                        {formik.touched.unitPrice && formik.errors.unitPrice && (
                                            <small className="p-error">{formik.errors.unitPrice}</small>
                                        )}
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <div className="field">
                                            <label htmlFor="usage" className="label-semi-bold">{t('usage')}</label>
                                            <span className="p-input-icon-left">                                                
                                                <InputTextarea autoResize id="usage" name="usage"
                                                    value={formik.values.usage}
                                                   onChange={(e) => {
                                                        const cleanedValue = e.target.value.replace(/\s\s+/g, ' ');
                                                        formik.setFieldValue('usage', cleanedValue);
                                                    }}
                                                    style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                    className={`pl-1 ${classNames({ 'p-invalid': formik.errors.usage })}`}
                                                    placeholder="Usage" rows={4} cols={30}/>
                                            </span>
                                        </div>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <div className="field">
                                            <label htmlFor="description" className="label-semi-bold">{t('description')}</label>
                                            <span className="p-input-icon-left">                                                
                                                <InputTextarea autoResize id="description" name="description"
                                                    value={formik.values.description}
                                                    onChange={(e) => {
                                                      const cleanedValue = e.target.value.replace(/\s\s+/g, ' ');
                                                      formik.setFieldValue('description', cleanedValue);
                                                    }}
                                                    style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                    className={`pl-1 ${classNames({ 'p-invalid': formik.errors.description })}`}
                                                    placeholder="Enter Description" rows={4} cols={30} />
                                            </span>
                                        </div>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <div className="field">
                                            <label htmlFor="code" className="label-semi-bold">{t('code')}</label>
                                            <span className="p-input-icon-left">
                                                <InputText type="text" id="code" name="code"
                                                    value={formik.values.code}
                                                    style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                    onChange={(e) => {
                                                        const cleanedValue = e.target.value.replace(/\s\s+/g, ' ');
                                                        formik.setFieldValue('code', cleanedValue);
                                                    }}
                                                    className={`pl-1 ${classNames({ 'p-invalid': formik.errors.code })}`}
                                                    placeholder="Enter Code" />
                                            </span>
                                        </div>
                                    </div>

                                    <div className="field col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <label htmlFor="weight" className="label-semi-bold">{t('weight')}<span className="required">*</span></label>
                                        <span className="p-input-icon-left">
                                            <InputNumber
                                                id="weight"
                                                name="weight"
                                                value={formik.values.weight}
                                                onBlur={formik.handleBlur}
                                                onValueChange={e => {
                                                    formik.setFieldValue('weight', e.value);
                                                }}
                                                mode="decimal"
                                                placeholder="Weight"
                                                style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                            />
                                        </span>
                                        {formik.touched.weight && formik.errors.weight && (
                                            <small className="p-error">{formik.errors.weight}</small>
                                        )}
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <label htmlFor="volume" className="label-semi-bold">{t('Volume')}<span className="required">*</span></label>
                                        <span className="p-input-icon-left">
                                            <InputNumber
                                                id="volume"
                                                name="volume"
                                                value={formik.values.volume}
                                                onBlur={formik.handleBlur}
                                                onValueChange={e => {
                                                    formik.setFieldValue('volume', e.value);
                                                }}
                                                placeholder="Enter Volume"
                                                maxFractionDigits={2}
                                                style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                            />
                                        </span>
                                        {formik.touched.volume && formik.errors.volume && (
                                            <small className="p-error">{formik.errors.volume}</small>
                                        )}
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <label htmlFor="measurement" className="label-semi-bold">{t('Measurement')}<span className="required">*</span></label>
                                        <span className="p-input-icon-left">
                                            <Dropdown
                                                id="measurement"
                                                name="measurement"
                                                value={formik.values.measurement}
                                                options={[
                                                    { label: "ml", value: "ml" },
                                                    { label: "gms", value: "gms" }
                                                ]}
                                                style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                onBlur={formik.handleBlur}
                                                onChange={(e) => {
                                                    formik.setFieldValue('measurement', e.value);
                                                    formik.setFieldTouched('measurement', true, false);
                                                }}
                                                placeholder="Select Unit (ml/gms)"
                                                className={`pl-1 w-full ${classNames({ 'p-invalid': formik.touched.measurement && formik.errors.measurement })}`}
                                            />
                                        </span>
                                        {formik.touched.measurement && formik.errors.measurement && (
                                            <small className="p-error">{formik.errors.measurement}</small>
                                        )}
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <label htmlFor="minOrderQty" className="label-semi-bold">{t('min_order_qty')}<span className="required">*</span></label>
                                        <span className="p-input-icon-left">
                                            <InputNumber
                                                id="minOrderQty"
                                                name="minOrderQty"
                                                value={formik.values.minOrderQty}
                                                onBlur={formik.handleBlur}
                                                onValueChange={e => {
                                                    formik.setFieldValue('minOrderQty', e.value);
                                                }}
                                                mode="decimal"
                                                placeholder="Enter Min Quantity"
                                                maxFractionDigits={2}
                                                style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                            />
                                        </span>
                                        {formik.touched.minOrderQty && formik.errors.minOrderQty && (
                                            <small className="p-error">{formik.errors.minOrderQty}</small>
                                        )}
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <label htmlFor="orderMultiples" className="label-semi-bold">{t('order_multiples')}<span className="required">*</span></label>
                                        <span className="p-input-icon-left">
                                            <InputText type="text" name="orderMultiples" id="orderMultiples"
                                                value={formik.values.orderMultiples}
                                                keyfilter="pnum"
                                                min={0} max={100}
                                                style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (/^\d*\.?\d{0,2}$/.test(value)) {
                                                        formik.setFieldValue('orderMultiples', value);
                                                    }
                                                }}
                                                onBlur={formik.handleBlur}
                                                className={`pl-1 ${classNames({ 'p-invalid': formik.touched.orderMultiples && formik.errors.orderMultiples })}`}
                                                placeholder="Order Multiples" />
                                        </span>
                                        {formik.touched.orderMultiples && formik.errors.orderMultiples && (
                                            <small className="p-error">{formik.errors.orderMultiples}</small>
                                        )}
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <label htmlFor="gst" className="label-semi-bold">{t('gst')}<span className="required">*</span></label>
                                        <span className="p-input-icon-left">
                                            <InputText type="text" name="gst" id="gst"
                                                value={formik.values.gst}
                                                keyfilter="pnum"
                                                style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (value === '' || /^-?\d{0,2}(\.\d{0,2})?$/.test(value)) {
                                                        formik.setFieldValue('gst', value);
                                                    }
                                                }}
                                                maxLength={5}
                                                onBlur={formik.handleBlur}
                                                className={`pl-1 ${classNames({ 'p-invalid': formik.touched.gst && formik.errors.gst })}`}
                                                placeholder="GST" />
                                        </span>
                                        {formik.touched.gst && formik.errors.gst && (
                                            <small className="p-error">{formik.errors.gst}</small>
                                        )}
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <label htmlFor="hsN_Code" className="label-semi-bold">{t('hsn_code')}</label>
                                        <span className="p-input-icon-left">
                                            <InputText type="text" name="hsN_Code" id="hsN_Code"
                                                value={formik.values.hsN_Code}
                                                onChange={formik.handleChange}
                                                keyfilter="alphanum"
                                                style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                className={`pl-1 ${classNames({ 'p-invalid': formik.errors.hsN_Code })}`}
                                                placeholder="HSN Code" />
                                        </span>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <label htmlFor="subscriptionAvailable" className="label-semi-bold">{t('subscription_available')}</label>
                                        <span className="p-input-icon-left">
                                            <div className="field-checkbox col-12">
                                                    <Checkbox
                                                        id="subscriptionAvailable"
                                                        name="subscriptionAvailable"
                                                        onChange={(e) => {
                                                            formik.setFieldValue('subscriptionAvailable', e.checked);
                                                        }}
                                                        checked={formik.values.subscriptionAvailable}
                                                        style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                    />
                                                    <label htmlFor="subscriptionAvailable">{t('subscription_available')} </label>
                                                </div>
                                        </span>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <label htmlFor="activeStartDate" className="label-semi-bold">{t('active_start_date')}</label>
                                        <span className="p-input-icon-left">
                                            <InputText
                                                type="date"
                                                name="activeStartDate"
                                                id="activeStartDate"
                                                value={formik.values.activeStartDate}
                                                onChange={formik.handleChange}
                                                onFocus={(e) => e.target.showPicker?.()}
                                                onKeyDown={(e) => e.preventDefault()}
                                                className={`pl-1 ${classNames({ 'p-invalid': formik.errors.activeStartDate })}`}
                                                placeholder="Enter Active Start Date"
                                                style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                            />

                                        </span>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <label htmlFor="activeEndDate" className="label-semi-bold">{t('active_end_date')}</label>
                                        <span className="p-input-icon-left">
                                            <InputText
                                            type="date"
                                            name="activeEndDate"
                                            id="activeEndDate"
                                            value={formik.values.activeEndDate}
                                            min={formik.values.activeStartDate || ""}   // ✅ This prevents selecting a lower date
                                            onFocus={(e) => e.target.showPicker?.()}
                                            onKeyDown={(e) => e.preventDefault()}
                                            onChange={formik.handleChange}
                                            className={`pl-1 ${classNames({ 'p-invalid': formik.errors.activeEndDate })}`}
                                            style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                            placeholder="Enter Active End Date"
                                        />

                                        </span>
                                      
                                            {formik.touched.activeEndDate && formik.errors.activeEndDate && (
                                                <small className="p-error">{formik.errors.activeEndDate}</small>
                                            )}
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <label htmlFor="leadTime" className="label-semi-bold">{t('lead_time')}</label>
                                        <span className="p-input-icon-left">
                                            <InputText type="date" name="leadTime" id="leadTime"
                                                value={formik.values.leadTime}
                                                onFocus={(e) => e.target.showPicker?.()}
                                                onKeyDown={(e) => e.preventDefault()}
                                                onChange={formik.handleChange}
                                                className={`pl-1 ${classNames({ 'p-invalid': formik.errors.leadTime })}`}
                                                style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                placeholder="Lead Time" />
                                        </span>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <label htmlFor="canExchange" className="label-semi-bold">{t('can_exchange')}</label>
                                        <span className="p-input-icon-left">
                                            <div className="field-checkbox col-12">
                                                <Checkbox
                                                    id="canExchange"
                                                    name="canExchange"
                                                    onChange={(e) => {
                                                        formik.setFieldValue('canExchange', e.checked);
                                                    }}
                                                    checked={formik.values.canExchange}
                                                    style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                />
                                                <label htmlFor="canExchange">{t('can_exchange')} </label>
                                            </div>
                                        </span>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <label htmlFor="canReturn" className="label-semi-bold">{t('can_return')}</label>
                                        <span className="p-input-icon-left">
                                            <div className="field-checkbox col-12">
                                                    <Checkbox
                                                        id="canReturn"
                                                        name="canReturn"
                                                        onChange={(e) => {
                                                            formik.setFieldValue('canReturn', e.checked);
                                                        }}
                                                        checked={formik.values.canReturn}
                                                        style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                    />
                                                    <label htmlFor="canReturn">{t('can_return')} </label>
                                                </div>
                                        </span>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <label htmlFor="courierAvailable" className="label-semi-bold">{t('Is Courier Available?')}</label>
                                        <span className="p-input-icon-left">
                                            <div className="field-checkbox col-12">
                                                    <Checkbox
                                                        id="courierAvailable"
                                                        name="courierAvailable"
                                                        onChange={(e) => {
                                                            formik.setFieldValue('courierAvailable', e.checked);
                                                        }}
                                                        checked={formik.values.courierAvailable}
                                                        style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                    />
                                                    <label htmlFor="courierAvailable">{t('courier_available')} </label>
                                                </div>
                                        </span>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <label htmlFor="shelfLife" className="label-semi-bold">{t('shelf_life')}</label>
                                        <span className="p-input-icon-left">
                                            <InputText type="text" name="shelfLife" id="shelfLife"
                                                value={formik.values.shelfLife}
                                               onChange={(e) => {
                                                        const cleanedValue = e.target.value.replace(/\s\s+/g, ' ');
                                                        formik.setFieldValue('shelfLife', cleanedValue);
                                                    }}
                                                className={`pl-1 ${classNames({ 'p-invalid': formik.errors.shelfLife })}`}
                                                style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                placeholder="Shelf Life" />
                                        </span>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <label htmlFor="inStock" className="label-semi-bold">{t('in_stock')}</label>
                                        <span className="p-input-icon-left">
                                            <InputText type="text" name="inStock" id="inStock"
                                                value={formik.values.inStock}
                                                keyfilter="pint"
                                                onChange={formik.handleChange}
                                                className={`pl-1 ${classNames({ 'p-invalid': formik.errors.inStock })}`}
                                                style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                placeholder="In Stock" />
                                        </span>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <label htmlFor="isDeleted" className="label-semi-bold">{t('is_deleted')}</label>
                                        <span className="p-input-icon-left">
                                            <div className="field-checkbox col-12">
                                                    <Checkbox
                                                        id="isDeleted"
                                                        name="isDeleted"
                                                        onChange={(e) => {
                                                            formik.setFieldValue('isDeleted', e.checked);
                                                        }}
                                                        checked={formik.values.isDeleted}
                                                        style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                    />
                                                    <label htmlFor="isDeleted">{t('is_deleted')} </label>
                                                </div>
                                        </span>
                                    </div>

                                    <div className="col-12 md:col-6 lg:col-6 mb-3 px-3">
                                        <label htmlFor="isHomeDeliveryAvailable" className="label-semi-bold">{t('Home Delivery')}</label>
                                        <span className="p-input-icon-left">
                                            <div className="field-checkbox col-12">
                                                    <Checkbox
                                                        id="isHomeDeliveryAvailable"
                                                        name="isHomeDeliveryAvailable"
                                                        onChange={(e) => {
                                                            formik.setFieldValue('isHomeDeliveryAvailable', e.checked);
                                                        }}
                                                        checked={formik.values.isHomeDeliveryAvailable}
                                                        style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
                                                    />
                                                    <label htmlFor="isHomeDeliveryAvailable">{t('Home Delivery')} </label>
                                                </div>
                                        </span>
                                    </div>

                                </div>
                            </div>
                        </div>
                        <Button type="submit" label="Save" className="right-align" > </Button>
                    </form>
                </div>
            </div>
        </>  
    )
}

export default ProductInfo